import { FC } from 'react';
import { Statistic } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FinancialValue } from '@/shared/components/financial-values';
import { dayjs } from '@/utilities';

import { FinancialOverviewMarketStatisticProps } from '../../types';

const rangeLabel = {
    realTime: 'Today',
    '1D': 'Past day',
    '5D': 'Past 5 days',
    '1W': 'Past week',
    '1M': 'Past month',
    QTD: 'Quarter to date',
    '1Q': 'Past quarter',
    '6M': 'Past 6 months',
    YTD: 'Year to date',
    '1Y': 'Past year',
    '2Y': 'Past 2 years',
    '5Y': 'Past 5 years',
    '10Y': 'Past 10 years',
    Max: 'All available history',
    ALL: 'All available history',
};

const numeric = (value: number | string | undefined) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
};

const formatNumericChange = (value: number) =>
    value.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });

export const FinancialOverviewMarketStatistic: FC<FinancialOverviewMarketStatisticProps> = ({
    chartRange,
    data,
    marketPrice,
    marketPriceAsOf,
    priceAction,
}) => {
    const { token } = useAntDesignTheme();
    const first = data.find((point) => numeric(point.close) !== undefined);
    const last = [...data].reverse().find((point) => numeric(point.close) !== undefined);
    const firstPrice = numeric(first?.close);
    const lastPrice = numeric(last?.close);
    const displayedPrice = numeric(marketPrice) ?? lastPrice;
    const change = firstPrice !== undefined && displayedPrice !== undefined ? displayedPrice - firstPrice : undefined;
    const changePercent = change !== undefined && firstPrice ? (change / firstPrice) * 100 : undefined;
    const positive = (change ?? 0) >= 0;

    return (
        <div style={{ display: 'grid', justifyItems: 'end', minWidth: '15rem', textAlign: 'right' }}>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    justifyContent: 'flex-end',
                }}
            >
                <Statistic
                    formatter={() => <FinancialValue scalable={false} value={displayedPrice} />}
                    value={displayedPrice ?? 0}
                    valueStyle={{
                        fontSize: token.fontSizeHeading3,
                        fontWeight: token.fontWeightStrong,
                        lineHeight: 1.2,
                    }}
                />
                {change !== undefined && changePercent !== undefined ? (
                    <span style={{ color: positive ? token.colorSuccess : token.colorError }}>
                        {positive ? '+' : ''}
                        {formatNumericChange(change)} ({positive ? '+' : ''}
                        {formatNumericChange(changePercent)}%)
                    </span>
                ) : null}
                <small style={{ color: token.colorTextSecondary }}>{rangeLabel[chartRange]}</small>
                {priceAction}
            </div>
            <small style={{ color: token.colorTextTertiary, marginTop: '0.125rem' }}>
                {(marketPriceAsOf || last?.date) && dayjs(marketPriceAsOf ?? last?.date).isValid()
                    ? `As of ${dayjs(marketPriceAsOf ?? last?.date).format('MMM D, YYYY [at] h:mm A')}`
                    : 'Market price unavailable'}
            </small>
        </div>
    );
};
